/**
 * Search view. Reads `q` from the URL search params, runs the API search,
 * and renders the result list with snippet highlighting.
 *
 * Mode toggle: Quick (FTS only — fast, local, exact-match-ish) vs Smart
 * (hybrid: FTS + semantic, merged with FTS hits getting a +1 score bonus
 * so literal matches still float to the top). The standalone "semantic"
 * mode is no longer surfaced — Smart already includes it via merge, and
 * the rare pure-vector use case isn't worth the third option.
 *
 * The query is debounced before being passed to the API so a fast typist
 * doesn't fire an OpenAI embedding call per keystroke in Smart mode.
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { NoteCard } from "../components/NoteCard";
import { NoteEditor } from "../components/NoteEditor";
import { useConfig, useSearch } from "../api/hooks";
import type { Note } from "../api/types";

const searchSchema = z.object({ q: z.string().optional() });

export const Route = createFileRoute("/search")({
  validateSearch: (input) => searchSchema.parse(input),
  component: SearchPage,
});

type UiMode = "quick" | "smart";

/** UI mode → API mode. Smart maps to hybrid; the server falls back to text
 *  automatically when AI isn't configured, so the UI doesn't need to know. */
function apiModeFor(ui: UiMode): "text" | "hybrid" {
  return ui === "quick" ? "text" : "hybrid";
}

/** Tiny debounce hook. Returns `value` after `delay` ms of stability. */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function SearchPage() {
  const { q } = Route.useSearch();
  const navigate = useNavigate({ from: "/search" });
  const cfgQ = useConfig();
  const aiEnabled = cfgQ.data?.ai_enabled ?? false;
  // Smart by default — it's hybrid behind the scenes, and falls back to
  // text-only on the server when AI isn't configured.
  const [mode, setMode] = useState<UiMode>("smart");
  const [editing, setEditing] = useState<Note | null>(null);

  const queryString = (q ?? "").trim();
  // 300ms debounce. Smart mode embeds the query via OpenAI on every fetch,
  // so we don't want to fire one call per keystroke.
  const debouncedQuery = useDebouncedValue(queryString, 300);
  const searchQ = useSearch(debouncedQuery, apiModeFor(mode));

  return (
    <div className="px-3 sm:px-6 py-4">
      <div className="flex items-center gap-2 mb-3">
        <input
          autoFocus
          type="search"
          value={q ?? ""}
          onChange={(e) => navigate({ search: () => ({ q: e.target.value }), replace: true })}
          placeholder="Search…"
          className="flex-1 px-3 py-2 rounded border text-sm"
          style={{ borderColor: "var(--border)", background: "var(--bg)" }}
        />
        {aiEnabled && (
          <div
            role="tablist"
            aria-label="Search mode"
            className="inline-flex rounded border overflow-hidden text-sm"
            style={{ borderColor: "var(--border)" }}
          >
            <ModeTab
              label="Quick"
              hint="Exact-text search (FTS5). Fastest."
              active={mode === "quick"}
              onClick={() => setMode("quick")}
            />
            <ModeTab
              label="Smart"
              hint="Text + AI semantic match, merged."
              active={mode === "smart"}
              onClick={() => setMode("smart")}
            />
          </div>
        )}
      </div>

      {!queryString && (
        <div className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Type to search across all your notes (titles, body, URLs).
          {aiEnabled
            ? " Smart mode also matches by meaning."
            : " Add an OpenAI key in Settings to enable Smart (semantic) search."}
        </div>
      )}

      <div className="notes-grid">
        {(searchQ.data ?? []).map((r) => (
          <NoteCard key={r.note.id} note={r.note} onClick={() => setEditing(r.note)} />
        ))}
      </div>

      {searchQ.data && searchQ.data.length === 0 && debouncedQuery && (
        <div className="text-sm mt-6" style={{ color: "var(--fg-muted)" }}>
          No matches.
        </div>
      )}

      {editing && <NoteEditor note={editing} open onClose={() => setEditing(null)} />}
    </div>
  );
}

function ModeTab({
  label,
  hint,
  active,
  onClick,
}: {
  label: string;
  hint: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      title={hint}
      className="px-3 py-2 font-medium"
      style={{
        background: active ? "var(--color-primary)" : "transparent",
        color: active ? "#1c1300" : "var(--fg)",
      }}
    >
      {label}
    </button>
  );
}
